/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component, { type ComponentAttrs } from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import listItems from 'flarum/common/helpers/listItems';
import Dropdown from 'flarum/common/components/Dropdown';
import ItemList from 'flarum/common/utils/ItemList';
import ReviewList from './ReviewList';
import LiteraryReviewsListState from '../states/LiteraryReviewsListState';
import setRouteWithForcedRefresh from 'flarum/common/utils/setRouteWithForcedRefresh';
import type Mithril from 'mithril';
import type Article from 'flarum/orell-articles/models/Article';
import type LiteraryWork from 'flarum/orell-articles/models/LiteraryWork';
import RatingsCard from './RatingsCard';
import CreateReviewActionButton from './CreateReviewActionButton';

export interface LiteraryWorkPageReviewsAttrs extends ComponentAttrs {
	/** The work whose reviews are listed. Rendered by `forum/extend/addReviewsToLiteraryWorkPage`. */
	work: LiteraryWork;
}

/** The URL parameters this list reads. Kept as a type alias so it can be spread into API params. */
type LiteraryWorkReviewsParams = {
	sort?: string;
	q?: string;
	filter?: string;
};

export default class LiteraryWorkPageReviews extends Component<LiteraryWorkPageReviewsAttrs, LiteraryReviewsListState> {
	oninit(vnode: Mithril.Vnode<LiteraryWorkPageReviewsAttrs, this>) {
		super.oninit(vnode);

		const params = this.params();
		const { work } = this.attrs;

		this.state = new LiteraryReviewsListState(params, 1, work);
		this.state.refresh();
	}

	view(): Mithril.Children {
		const { work } = this.attrs;
		// The work page always includes `mainArticle` (see `forum/extend/addReviewsToLiteraryWorkPage`).
		const article = work.mainArticle() as Article;
		return (
			<>
				<RatingsCard article={this.attrs.work.mainArticle() as Article} work={this.attrs.work} />
				<div className="LiteraryWorkPage-Reviews ArticlePage-reviews">
					<div className="IndexPage-toolbar">
						<ul className="IndexPage-toolbar-view">{listItems(this.viewItems().toArray())}</ul>
						<ul className="IndexPage-toolbar-action">{listItems(this.actionItems().toArray())}</ul>
					</div>
					<ReviewList state={this.state} noCover={true} />
					{this.state.getPages().flatMap((page) => page.items).length > 3 && (
						<div className="LiteraryWorkPage-Reviews-create-review-button">
							<CreateReviewActionButton article={article} reviewsState={this.state} />
						</div>
					)}
				</div>
			</>
		);
	}

	viewItems() {
		const items = new ItemList<Mithril.Children>();
		const sortMap = this.state.sortMap();

		const sortOptions: Record<string, Mithril.Children> = {};
		for (const i in sortMap) {
			sortOptions[i] = app.translator.trans('core.forum.index_sort.' + i + '_button');
		}

		items.add(
			'sort',
			<Dropdown
				buttonClassName="Button"
				label={sortOptions[this.params().sort!] || Object.keys(sortMap).map((key) => sortOptions[key])[0]}
				accessibleToggleLabel={app.translator.trans('core.forum.index_sort.toggle_dropdown_accessible_label') as string}
			>
				{Object.keys(sortOptions).map((value) => {
					const label = sortOptions[value];
					const active = (this.params().sort || Object.keys(sortMap)[0]) === value;

					return (
						<Button icon={active ? 'far fa-check' : true} onclick={this.changeSort.bind(this, value)} active={active}>
							{label}
						</Button>
					);
				})}
			</Dropdown>
		);

		return items;
	}

	actionItems() {
		const items = new ItemList<Mithril.Children>();

		items.add(
			'refresh',
			<Button
				title={app.translator.trans('core.forum.index.refresh_tooltip')}
				icon="far fa-sync"
				className="Button Button--icon"
				onclick={() => this.state.refresh()}
			/>
		);

		return items;
	}

	changeSort(sort: string) {
		const params = this.params();
		const { work } = this.attrs;

		if (sort === Object.keys(this.state.sortMap())[0]) {
			delete params.sort;
		} else {
			params.sort = sort;
		}

		// A work rendered by the work page is always persisted, so it has an id.
		setRouteWithForcedRefresh(app.route.of_literary_work(work.id()!, params));
	}

	stickyParams(): LiteraryWorkReviewsParams {
		return {
			sort: m.route.param('sort'),
			q: m.route.param('q'),
		};
	}

	params(): LiteraryWorkReviewsParams {
		const params = this.stickyParams();

		params.filter = m.route.param('filter');

		return params;
	}
}
